import { useState } from "react";
import { Button, Drawer, Empty, Tooltip } from "antd";
import { CheckCircleFilled, HistoryOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import profileReviewApi, {
  type ProfileReviewEntry,
} from "@/services/profileReviewApi";
import { MedicalReviewApiError } from "@/services/medicalReviewApi";
import { useAppNotification } from "@/hooks/useAppNotification";
import { useScopeParams } from "@/hooks/useScopeParams";

/**
 * The profile-level review fields, on the profile itself: whether this
 * participant's profile has been reviewed, by whom and when, a button to mark
 * it (or take the review back), and the full ledger behind a History drawer.
 */
export default function ProfileReviewControl({
  subjectId,
}: {
  subjectId: string;
}) {
  const { id: trialId } = useScopeParams();
  const [historyOpen, setHistoryOpen] = useState(false);
  const queryClient = useQueryClient();
  const { openErrorNotification, contextHolder } = useAppNotification();

  const { data } = useQuery({
    queryKey: ["participant-profile-review", trialId, subjectId],
    queryFn: () => profileReviewApi.get(trialId!, subjectId),
    enabled: Boolean(trialId && subjectId),
  });

  const markMutation = useMutation({
    mutationFn: (action: "reviewed" | "review_reopened") =>
      profileReviewApi.mark(trialId!, subjectId, { action }),
    onSuccess: (state) =>
      queryClient.setQueryData(
        ["participant-profile-review", trialId, subjectId],
        state,
      ),
    onError: (error: MedicalReviewApiError) =>
      openErrorNotification({
        title: "Could not record the review",
        description: error.message,
      }),
  });

  if (!data) return null;

  const signature = data.reviewed ? data.last_reviewed : null;

  return (
    <div className="flex flex-col gap-1.5" data-testid="profile-review-control">
      {contextHolder}
      {signature ? (
        <>
          <span className="flex items-start gap-1.5 rounded-lg bg-[#E4F1EA] px-2.5 py-1.5 text-xs font-medium text-[#2E7D5B]">
            <CheckCircleFilled className="mt-0.5" />
            <span>
              Reviewed by {signature.by}
              {signature.role ? ` · ${signature.role}` : ""} on{" "}
              {new Date(signature.at).toLocaleString()}
            </span>
          </span>
          <div className="flex items-center gap-1">
            <Tooltip title="Take this review back. It returns the profile to unreviewed; the original review and the reopening both stay in the history.">
              <Button
                size="small"
                loading={markMutation.isPending}
                onClick={() => markMutation.mutate("review_reopened")}
              >
                Reopen
              </Button>
            </Tooltip>
            <HistoryButton
              count={data.history.length}
              onOpen={setHistoryOpen}
            />
          </div>
        </>
      ) : (
        <div className="flex items-center gap-1">
          <Tooltip title="Records that you have reviewed this participant's profile — your name, your role, and the time are kept in the review history.">
            <Button
              size="small"
              type="primary"
              className="flex-1"
              loading={markMutation.isPending}
              onClick={() => markMutation.mutate("reviewed")}
            >
              Mark profile reviewed
            </Button>
          </Tooltip>
          <HistoryButton count={data.history.length} onOpen={setHistoryOpen} />
        </div>
      )}

      <Drawer
        title={`Review history · ${subjectId}`}
        placement="right"
        width={420}
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
      >
        <ReviewHistory entries={data.history} />
      </Drawer>
    </div>
  );
}

function HistoryButton({
  count,
  onOpen,
}: {
  count: number;
  onOpen: (open: boolean) => void;
}) {
  if (count === 0) return null;
  return (
    <Button
      size="small"
      type="text"
      icon={<HistoryOutlined />}
      onClick={() => onOpen(true)}
    >
      History ({count})
    </Button>
  );
}

function ReviewHistory({ entries }: { entries: ProfileReviewEntry[] }) {
  if (entries.length === 0) {
    return (
      <Empty
        description="Nobody has reviewed this profile yet."
        className="my-8"
      />
    );
  }
  return (
    <div className="flex flex-col gap-2">
      {entries.map((entry) => (
        <div
          key={entry.id}
          className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b border-dashed border-[#E6ECF1] pb-1.5 text-xs"
        >
          <span
            className={`font-semibold ${
              entry.action === "reviewed" ? "text-[#2E7D5B]" : "text-[#A4720F]"
            }`}
          >
            {entry.action === "reviewed" ? "Reviewed" : "Review reopened"}
          </span>
          <span className="font-medium text-[#2B4752]">{entry.by}</span>
          {entry.role && <span className="text-[#8FA5A0]">{entry.role}</span>}
          <span className="text-[#556C75]">
            {new Date(entry.at).toLocaleString()}
          </span>
          {entry.comment && (
            <span className="w-full text-[#556C75]">{entry.comment}</span>
          )}
        </div>
      ))}
    </div>
  );
}
